import React, { CSSProperties, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { areEqual } from 'react-window';

import { localize } from 'common/utils';
import { ItemDragOverlay } from 'modules/menu/components/ItemDragOverlay';
import { OfferInventoryDataResponse } from 'modules/shared-menu/types';

import { ItemRow, StyledCaretRight, StyledCategorySubheader, StyledOfferHeader, StyledOptionHeader } from './styled';
import { getCategoryBulkSelectionStatus } from './utils';
import { SharedMenuElement } from '../../types';
import { isSharedMenuOffer } from '../../utils';
import { transformOfferInventoryToQuantity } from '../OfferDetailsForm/utils';
import DragOverlayOfferHeader from '../OfferHeader/DragOverlayOfferHeader';
import MenuEditorDnDItem from '../dnd/MenuEditorDnDItem';
import { MenuEditorDragData } from '../dnd/types';

export type ListRowRendererProps = {
  index: number;
  data: {
    itemList: SharedMenuElement[];
    activeOfferId?: string | undefined;
    canDrop?: boolean;
    canDrag?: boolean;
    draggingId?: string;
    draggingType?: MenuEditorDragData['type'];
    primaryLanguage: Language;
    onOfferClick?: (id: string) => void;
    onCopyOffer?: (id: string) => void;
    activeOptionId?: string | undefined;
    onOptionClick?: (id: string) => void;
    onCopyOption?: (id: string) => void;
    onSelect?: (ids: string[]) => void;
    onDeselect?: (ids: string[]) => void;
    selectedIds?: Set<string>;
    offerInventory?: OfferInventoryDataResponse[];
  };
  style: CSSProperties;
};

const ListRowRenderer = ({
  index,
  data: {
    itemList,
    activeOfferId,
    activeOptionId,
    canDrag,
    canDrop,
    draggingId,
    draggingType,
    primaryLanguage,
    onOfferClick,
    onCopyOffer,
    onOptionClick,
    onCopyOption,
    onSelect,
    onDeselect,
    selectedIds,
    offerInventory,
  },
  style,
}: ListRowRendererProps) => {
  const element = useMemo(() => itemList[index], [itemList, index]);
  const handleSelectOffer = useCallback(
    (offerId: string) => {
      onSelect?.([offerId]);
    },
    [onSelect],
  );

  const handleDeselectOffer = useCallback(
    (offerId: string) => {
      onDeselect?.([offerId]);
    },
    [onDeselect],
  );

  const isOfferSelected = useMemo(() => {
    if (element.type === 'item') {
      return !!selectedIds?.has(element.itemState.id);
    }
    return false;
  }, [selectedIds, element]);

  const handleOfferClick = useCallback(
    (event: React.MouseEvent, offerId: string) => {
      if (event.metaKey || event.ctrlKey) {
        event.stopPropagation();
      } else {
        onOfferClick?.(offerId);
      }
    },
    [onOfferClick],
  );

  const getOfferIdsByCategory = useCallback(
    (categoryId?: string) => {
      const offerList = itemList.filter(isSharedMenuOffer);
      const itemsByCategory = categoryId
        ? offerList.filter(offer => offer.category?.id === categoryId)
        : offerList.filter(offer => !offer.category);
      return itemsByCategory.map(offer => offer.itemState.id);
    },
    [itemList],
  );

  const offerQuantity = useMemo(() => {
    if (!offerInventory || element.type !== 'item') {
      return;
    }
    const offerQuantity = offerInventory?.find(inventory => String(inventory.offer_id) === element.itemState.id);
    return offerQuantity ? transformOfferInventoryToQuantity(offerQuantity) : undefined;
  }, [element, offerInventory]);

  if (element.type === 'category-subheader' || element.type === 'subcategory-subheader') {
    const { category } = element;
    const offerIds = getOfferIdsByCategory(category?.id);
    const selectionStatus = getCategoryBulkSelectionStatus(offerIds, selectedIds);

    return (
      <ItemRow style={style}>
        <StyledCategorySubheader
          header={
            element.category ? (
              <>
                {element.parentCategory && (
                  <>
                    {localize(element.parentCategory.name, primaryLanguage)}
                    <StyledCaretRight />
                  </>
                )}
                {localize(element.category.name, primaryLanguage)}
              </>
            ) : (
              <FormattedMessage id="menu.nonCategorised" />
            )
          }
          primaryLanguage={primaryLanguage}
          disableSelection={!!activeOfferId || !category?.offer_bindings?.length}
          onDeselect={() => onDeselect?.(offerIds)}
          onSelect={() => onSelect?.(offerIds)}
          selectionStatus={selectionStatus}
        />
      </ItemRow>
    );
  }

  if (element.type === 'item') {
    const hasCategory = !!element.category;
    const isBulkDragging =
      !!draggingId &&
      selectedIds &&
      selectedIds.has(draggingId) &&
      selectedIds.has(element.itemState.id) &&
      selectedIds.size > 1;
    const disableAssetDrop = draggingType !== 'DND_IMAGE_GALLERY_ASSET';

    const dndData: MenuEditorDragData =
      selectedIds && selectedIds.size > 1 && selectedIds.has(element.itemState.id)
        ? {
            type: hasCategory ? 'DND_EDITOR_MULTIPLE_ITEMS' : 'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
            selectedIds,
            dragOverlay: <ItemDragOverlay count={selectedIds.size} />,
          }
        : {
            type: hasCategory ? 'DND_EDITOR_SINGLE_ITEM' : 'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
            dragOverlay: (
              <DragOverlayOfferHeader
                category={element.category}
                item={element.itemState}
                primaryLanguage={primaryLanguage}
                selected={!!selectedIds?.has(element.itemState.id)}
                itemInventoryCount={offerQuantity}
              />
            ),
          };

    return (
      <ItemRow style={style}>
        <MenuEditorDnDItem
          id={element.itemState.id}
          disabled={{
            droppable: !canDrop,
            draggable: !canDrag,
          }}
          data={dndData}
        >
          {({ isDragging }) => (
            <StyledOfferHeader
              active={element.itemState.id === activeOfferId}
              category={element.category}
              offer={element.itemState}
              hidden={isDragging || isBulkDragging}
              disabledSelection={!!activeOfferId}
              disableAssetDrop={disableAssetDrop}
              onSelect={handleSelectOffer}
              onDeselect={handleDeselectOffer}
              selected={isOfferSelected}
              onCopy={onCopyOffer}
              onClick={handleOfferClick}
              itemInventoryCount={offerQuantity}
            />
          )}
        </MenuEditorDnDItem>
      </ItemRow>
    );
  }

  if (element.type === 'option') {
    return (
      <ItemRow style={style}>
        <StyledOptionHeader
          active={element.optionState.id === activeOptionId}
          hideVat
          onCopy={onCopyOption}
          onClick={event => {
            if (event.metaKey || event.ctrlKey) {
              event.stopPropagation();
            } else {
              onOptionClick?.(element.optionState.id);
            }
          }}
          option={element.optionState}
          orderDisabledChoicesFirst
          primaryLanguage={primaryLanguage}
        />
      </ItemRow>
    );
  }

  if (element.type === 'options-subheader') {
    return (
      <ItemRow style={style}>
        <StyledCategorySubheader header={element.name} primaryLanguage={primaryLanguage} />;
      </ItemRow>
    );
  }

  return null;
};

export default React.memo(ListRowRenderer, areEqual);
